/**
 * Anular un movimiento cargado a mano (0155).
 *
 * Antes no habia forma de corregir un gasto mal cargado: se compensaba con un
 * «Otro» al reves, que dejaba Ganancias mal (un «Otro» egreso restaba y un
 * «Otro» ingreso no sumaba). Anular asienta el espejo con el mismo concepto: la
 * caja vuelve a donde estaba, los dos renglones quedan a la vista, y Ganancias
 * y Socios dejan afuera a los dos.
 */
import { useEffect, useState } from "react";
import { formatear } from "@tora/shared";

import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { formatearFechaHora } from "@/lib/utils";
import { aNumero, type CuentaFinanciera, type MovimientoCaja } from "@/lib/tipos";

import { Fila } from "./DialogMovimiento";
import { ETIQUETA_CONCEPTO, nombreCuenta } from "./logica";

export interface DialogAnularMovimientoProps {
  /** El movimiento a anular; null con el dialogo cerrado. */
  movimiento: MovimientoCaja | null;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  guardando?: boolean;
  onCancelar: () => void;
  onConfirmar: (motivo: string) => void;
}

export function DialogAnularMovimiento({
  movimiento,
  cuentas,
  guardando = false,
  onCancelar,
  onConfirmar,
}: DialogAnularMovimientoProps): JSX.Element {
  const [motivo, setMotivo] = useState("");
  useEffect(() => {
    if (movimiento) setMotivo("");
  }, [movimiento]);

  const m = movimiento;
  return (
    <Dialog open={m !== null} onOpenChange={(a) => !a && onCancelar()}>
      <DialogContent tamano="sm" mobile="hoja">
        <DialogHeader>
          <DialogTitle>¿Anular este movimiento?</DialogTitle>
          <DialogDescription>
            Se asienta uno igual al revés: la caja vuelve a como estaba y deja de contar en
            Ganancias. Los dos quedan a la vista en la lista.
          </DialogDescription>
        </DialogHeader>

        {m ? (
          <div className="space-y-4">
            <dl className="space-y-2 text-sm">
              <Fila termino="Fecha" valor={formatearFechaHora(m.fecha)} />
              <Fila termino="Concepto" valor={ETIQUETA_CONCEPTO[m.concepto] ?? m.concepto} />
              <Fila termino="Cuenta" valor={nombreCuenta(cuentas, m.cuenta_id)} />
              <Fila
                termino="Importe"
                valor={
                  <span className="font-medium tabular-nums">
                    {m.tipo === "EGRESO" ? "− " : "+ "}
                    {formatear(aNumero(m.importe), m.moneda)} {m.moneda}
                  </span>
                }
              />
              {m.detalle ? <Fila termino="Detalle" valor={m.detalle} /> : null}
            </dl>
            <Campo label="Por qué se anula" ayuda="Opcional.">
              <Input
                id="anular-motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: cargado dos veces"
                autoComplete="off"
              />
            </Campo>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancelar}>
            Volver
          </Button>
          <Button
            type="button"
            variant="destructive"
            cargando={guardando}
            onClick={() => onConfirmar(motivo.trim())}
          >
            Anular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
